"use client";

import { useRef, useCallback, useEffect, useState } from "react";
import type { Slide } from "@/entities/slide";

const SLIDE_UNIT = 1; // each slide corresponds to 1 unit in scroll offset
const WHEEL_SENSITIVITY = 0.01; // slides per pixel of wheel delta
const SNAP_DURATION = 500; // ms for snap animation

const SECTION_COLORS: Record<string, string> = {
  help: "var(--help)",
  school: "var(--school)",
  media: "var(--media)",
};

/* ── Tab bar ── */
function TabBar({
  current,
  onGoTo,
  slides,
}: {
  current: number;
  onGoTo: (i: number) => void;
  hasSection: boolean;
  hovered: boolean;
  slides: Slide[];
}) {
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Native wheel → horizontal scroll (fires before parent's bubble-phase listener)
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      el.scrollLeft += e.deltaY + e.deltaX;
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // On every `current` change, always ensure the active tab is centered/visible.
  // User manual scroll of the tab rail does not change `current`, so this effect
  // won't fire from that path — no guard needed.
  // requestAnimationFrame defers until after paint so offsetLeft is correct even
  // on fresh mount (e.g. returning from a section page to home).
  useEffect(() => {
    const btn = tabButtonRefs.current[current];
    const container = tabsRef.current;
    if (!btn || !container) return;
    requestAnimationFrame(() => {
      const btn2 = tabButtonRefs.current[current];
      const container2 = tabsRef.current;
      if (!btn2 || !container2) return;
      const btnCenter = btn2.offsetLeft + btn2.offsetWidth / 2;
      const containerCenter = container2.clientWidth / 2;
      const targetLeft = btnCenter - containerCenter;
      const maxScroll = container2.scrollWidth - container2.clientWidth;
      const clampedLeft = Math.max(0, Math.min(maxScroll, targetLeft));
      if (Math.abs(container2.scrollLeft - clampedLeft) > 2) {
        container2.scrollTo({ left: clampedLeft, behavior: "smooth" });
      }
    });
  }, [current]);

  return (
    <div
      ref={tabsRef}
      className="no-scrollbar"
      style={{
        position: "absolute",
        top: "var(--fluid-space-xl)",
        left: "var(--fluid-space-lg)",
        right: "var(--fluid-space-lg)",
        display: "flex",
        gap: "16px",
        justifyContent: "flex-start",
        zIndex: 10,
        transition: "left 0.38s cubic-bezier(0.25,0.1,0.25,1)",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        paddingBottom: "4px", // for scrollbar/focus
        pointerEvents: "auto",
        touchAction: "pan-x",
      }}
      data-tabs
    >
      {slides.map((slide, i) => {
        const isActive = i === current;
        const tabSectionColor = slide.sectionId
          ? SECTION_COLORS[slide.sectionId]
          : null;

        return (
          <button
            key={i}
            ref={(el) => {
              tabButtonRefs.current[i] = el;
            }}
            className="group relative border-none bg-transparent"
            onClick={(e) => {
              e.stopPropagation();
              if (i !== current) {
                onGoTo(i);
              }
            }}
            style={{
              flex: "0 0 auto",
              height: 32,
              padding: "0 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            {/* Base gray underline */}
            <div
              className="bg-border"
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: isActive ? 2 : 1,
                transition: "height 0.25s ease",
              }}
            />
            {/* Colored underline (active or hover) */}
            <div
              className={`absolute bottom-0 left-0 right-0 h-[2px] bg-no-repeat transition-[background-size] duration-300 ease-out ${
                isActive
                  ? "bg-[length:100%_2px] bg-left-bottom"
                  : "bg-[length:0%_2px] bg-right-bottom [@media(hover:hover)]:group-hover:bg-[length:100%_2px] [@media(hover:hover)]:group-hover:bg-left-bottom"
              }`}
              style={{
                backgroundImage: `linear-gradient(to right, ${tabSectionColor || "var(--group)"}, ${tabSectionColor || "var(--group)"})`,
              }}
            />
            <span
              className={`font-content text-fluid-xs whitespace-nowrap select-none overflow-hidden text-ellipsis transition-colors duration-300 ${
                isActive
                  ? "font-semibold text-foreground"
                  : "font-normal text-muted"
              }`}
            >
              {slide.tab}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Shared text block ── */
function SlideContent({
  slide,
  opacity,
}: {
  slide: Slide;
  opacity: number;
}) {
  return (
    <div style={{ opacity, transition: "none", overflow: "hidden" }}>
      <span className="font-content font-semibold text-fluid-2xl leading-tight text-foreground whitespace-pre-line block select-none">
        {slide.title}
      </span>
      <span
        className="font-content font-light text-foreground mt-fluid-sm whitespace-pre-line block select-none"
        style={{
          fontSize: "clamp(0.75rem, min(3vw, 10.5cqh), 1.1rem)",
          lineHeight: 1.3,
        }}
      >
        {slide.subtitle}
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */

export function GroupSlider({
  activeIndex,
  onChangeIndex,
  hovered = true,
  showEmma = false,
  slides,
}: {
  activeIndex: number;
  onChangeIndex: (i: number) => void;
  hovered?: boolean;
  showEmma?: boolean;
  slides: Slide[];
}) {
  const COUNT = slides.length;
  // Continuous scroll offset state — 0 = slide 0 at center, each slide is 1 unit.
  const [scrollOffset, setScrollOffset] = useState(activeIndex * SLIDE_UNIT);
  const scrollOffsetRef = useRef(scrollOffset);
  scrollOffsetRef.current = scrollOffset;

  const hoveredRef = useRef(hovered);
  hoveredRef.current = hovered;

  const snapTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const rafSnap = useRef<number>(undefined);
  const snapping = useRef(false);
  const instantJump = useRef(false); // flag: skip drum animation for tab clicks

  // Refs to slide DOM nodes for geometry logging
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Sync when activeIndex changes externally (e.g. bullet click)
  const prevActiveRef = useRef(activeIndex);
  // True while wheel/touch is in flight; cleared by scheduleSnap (120ms after last event).
  // Prevents intermediate React renders from triggering instant jumps in useEffect.
  const isInternalRef = useRef(false);

  /* ── Snap to nearest slide ── */
  const snapTo = useCallback(
    (targetIdx: number) => {
      const targetOffset = targetIdx * SLIDE_UNIT;
      const startOffset = scrollOffsetRef.current;
      const delta = targetOffset - startOffset;
      if (Math.abs(delta) < 0.1) {
        setScrollOffset(targetOffset);
        scrollOffsetRef.current = targetOffset; // BUG FIX: keep ref in sync so next wheel starts from correct base
        onChangeIndex(targetIdx);
        snapping.current = false;
        // Geometry log for immediate snaps
        requestAnimationFrame(() => {
          const container = containerRef.current;
          const cRect = container?.getBoundingClientRect();
          slides.forEach((_, si) => {
            const el = slideRefs.current[si];
            if (!el) return;
            const r = el.getBoundingClientRect();
            const inViewport = cRect
              ? r.top >= cRect.top - 1 && r.bottom <= cRect.bottom + 1
              : false;
          });
        });
        return;
      }
      snapping.current = true;
      const startTime = performance.now();

      const tick = (now: number) => {
        const elapsed = now - startTime;
        const t = Math.min(elapsed / SNAP_DURATION, 1);
        // ease-out cubic
        const ease = 1 - Math.pow(1 - t, 3);
        const current = startOffset + delta * ease;
        setScrollOffset(current);
        scrollOffsetRef.current = current;

        if (t < 1) {
          rafSnap.current = requestAnimationFrame(tick);
        } else {
          setScrollOffset(targetOffset);
          scrollOffsetRef.current = targetOffset;
          onChangeIndex(targetIdx);
          snapping.current = false;
          // Log geometry after snap completes (next frame so DOM reflects final transform)
          requestAnimationFrame(() => {
            const container = containerRef.current;
            const cRect = container?.getBoundingClientRect();
            slides.forEach((_, i) => {
              const el = slideRefs.current[i];
              if (!el) return;
              const r = el.getBoundingClientRect();
              const inViewport = cRect
                ? r.top >= cRect.top - 1 && r.bottom <= cRect.bottom + 1
                : false;
            });
          });
        }
      };
      rafSnap.current = requestAnimationFrame(tick);
    },
    [onChangeIndex],
  );

  const scheduleSnap = useCallback(() => {
    clearTimeout(snapTimer.current);
    snapTimer.current = setTimeout(() => {
      // Wheel/touch has stopped — clear internal guard so external changes work again
      isInternalRef.current = false;
      const nearestIdx = Math.round(scrollOffsetRef.current / SLIDE_UNIT);
      const clamped = Math.max(0, Math.min(COUNT - 1, nearestIdx));
      snapTo(clamped);
    }, 120);
  }, [snapTo]);

  /* ── Handle bullet / external index change ── */
  useEffect(() => {
    if (activeIndex !== prevActiveRef.current) {
      const targetOffset = activeIndex * SLIDE_UNIT;
      const diff = Math.abs(scrollOffsetRef.current - targetOffset);
      prevActiveRef.current = activeIndex;

      if (instantJump.current) {
        instantJump.current = false;
        return;
      }

      // Wheel/touch is still in flight — this is a React intermediate render, not an external change
      if (isInternalRef.current) {
        return;
      }

      // If offset is already close to target (scroll/drag drove us here), just snap
      if (diff < SLIDE_UNIT * 0.5) {
        if (rafSnap.current) cancelAnimationFrame(rafSnap.current);
        clearTimeout(snapTimer.current);
        snapping.current = false;
        snapTo(activeIndex);
      } else {
        // External jump (e.g. section tile click in App): instant
        if (rafSnap.current) cancelAnimationFrame(rafSnap.current);
        clearTimeout(snapTimer.current);
        snapping.current = false;
        setScrollOffset(targetOffset);
        scrollOffsetRef.current = targetOffset;
      }
    }
  }, [activeIndex, snapTo]);

  /* ── Wheel ── */
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (snapping.current) {
        // Cancel current snap
        if (rafSnap.current) cancelAnimationFrame(rafSnap.current);
        snapping.current = false;
      }

      const newOffset = scrollOffsetRef.current + e.deltaY * WHEEL_SENSITIVITY;
      const clampedOffset = Math.max(
        0,
        Math.min((COUNT - 1) * SLIDE_UNIT, newOffset),
      );
      setScrollOffset(clampedOffset);
      scrollOffsetRef.current = clampedOffset;

      // Update activeIndex live as we pass midpoints.
      // Compare against prevActiveRef (updated synchronously) — not stale closure activeIndex.
      const liveIdx = Math.round(clampedOffset / SLIDE_UNIT);
      const clampedIdx = Math.max(0, Math.min(COUNT - 1, liveIdx));
      if (clampedIdx !== prevActiveRef.current) {
        isInternalRef.current = true;
        prevActiveRef.current = clampedIdx;
        onChangeIndex(clampedIdx);
      }

      scheduleSnap();
    },
    [onChangeIndex, scheduleSnap],
  );

  /* ── Touch / Drag ── */
  const touchStartRef = useRef<{
    y: number;
    offset: number;
    inTabs: boolean;
  } | null>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (snapping.current) {
      if (rafSnap.current) cancelAnimationFrame(rafSnap.current);
      snapping.current = false;
    }
    const inTabs = !!(e.target as HTMLElement)?.closest("[data-tabs]");
    touchStartRef.current = {
      y: e.touches[0].clientY,
      offset: scrollOffsetRef.current,
      inTabs,
    };
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!touchStartRef.current) return;
      if (touchStartRef.current.inTabs) return;
      // Prevent default scrolling
      e.preventDefault();
      e.stopPropagation();

      const deltaY = touchStartRef.current.y - e.touches[0].clientY;
      // Adjust sensitivity for touch
      const touchSensitivity = 0.005;
      const newOffset =
        touchStartRef.current.offset + deltaY * touchSensitivity;
      const clampedOffset = Math.max(
        0,
        Math.min((COUNT - 1) * SLIDE_UNIT, newOffset),
      );

      setScrollOffset(clampedOffset);
      scrollOffsetRef.current = clampedOffset;

      const liveIdx = Math.round(clampedOffset / SLIDE_UNIT);
      const clampedIdx = Math.max(0, Math.min(COUNT - 1, liveIdx));
      if (clampedIdx !== prevActiveRef.current) {
        isInternalRef.current = true;
        prevActiveRef.current = clampedIdx;
        onChangeIndex(clampedIdx);
      }
    },
    [onChangeIndex],
  );

  const handleTouchEnd = useCallback(() => {
    touchStartRef.current = null;
    scheduleSnap();
  }, [scheduleSnap]);

  /* ── Event listeners ── */
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd);
    return () => {
      el.removeEventListener("wheel", handleWheel);
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd]);

  /* ── Keyboard ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        const target = Math.max(0, activeIndex - 1);
        if (target !== activeIndex) {
          prevActiveRef.current = target;
          onChangeIndex(target);
          if (rafSnap.current) cancelAnimationFrame(rafSnap.current);
          snapTo(target);
        }
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const target = Math.min(COUNT - 1, activeIndex + 1);
        if (target !== activeIndex) {
          prevActiveRef.current = target;
          onChangeIndex(target);
          if (rafSnap.current) cancelAnimationFrame(rafSnap.current);
          snapTo(target);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeIndex, onChangeIndex, snapTo]);

  /* ── Render — vertical stacking with proportional scaling ── */
  // Each slide i sits at offset (i * SLIDE_UNIT).
  // The "camera" is at `scrollOffset`. So slide i's vertical offset = (scrollOffset - i).
  // Slides whose offset is 0 are centered and at full scale. Others shift vertically and shrink.

  return (
    <div
      ref={containerRef}
      className="h-full w-full relative"
      style={
        {
          overflow: "hidden",
          touchAction: "pan-x", // Allow horizontal pan for tabs, handle vertical in JS
          cursor: "auto",
          "--slide-spacing": "120px",
        } as React.CSSProperties
      }
      onClick={(e) => e.stopPropagation()}
    >
      {slides.map((slide, i) => {
        const offset = scrollOffset - i;
        const absOffset = Math.abs(offset);
        // Only render slides within visible range
        if (absOffset > 2.5) return null;
        // Opacity: full at 0, fade to 0 at ±1 slide
        const fadeStart = 0.3;
        const fadeEnd = 1.0;
        const opacity =
          absOffset <= fadeStart
            ? 1
            : absOffset >= fadeEnd
              ? 0
              : 1 - (absOffset - fadeStart) / (fadeEnd - fadeStart);
        // Scale: reduce up to 20% per slide offset
        const scale = 1 - 0.2 * Math.min(1, absOffset);

        return (
          <div
            key={i}
            ref={(el) => { slideRefs.current[i] = el; }}
            className={showEmma ? "slide-emma-constrain" : ""}
            style={
              {
                position: "absolute",
                left: 0,
                right: 0,
                overflow: "hidden",
                // anchor zone starts below TabBar (xl padding + 32px button height + sm gap)
                top: "calc(var(--fluid-space-xl) + 32px + var(--fluid-space-sm))",
                bottom: 0,
                paddingLeft: "var(--fluid-space-lg)",
                paddingTop: "var(--fluid-space-lg)",
                // when showEmma: paddingRight is handled by .slide-emma-constrain class (md-responsive)
                // when !showEmma: set it here directly so no class override needed
                ...(showEmma ? {} : { paddingRight: "var(--fluid-space-lg)" }),
                display: "flex",
                flexDirection: "column",
                // flex-start: title is always pinned to the same Y anchor — no jump on subtitle length change
                justifyContent: "flex-start",
                pointerEvents: "none",
                "--offset": offset,
                "--scale": scale,
                transform: `translateY(calc(var(--offset) * -1 * var(--slide-spacing))) scale(var(--scale))`,
                willChange: "transform",
              } as React.CSSProperties
            }
          >
            <SlideContent slide={slide} opacity={opacity} />
          </div>
        );
      })}

      {/* ── Tab bar ── */}
      <TabBar
        current={activeIndex}
        onGoTo={(i) => {
          if (i !== activeIndex) {
            prevActiveRef.current = i;
            if (rafSnap.current) cancelAnimationFrame(rafSnap.current);
            clearTimeout(snapTimer.current);
            snapping.current = false;
            const targetOffset = i * SLIDE_UNIT;
            setScrollOffset(targetOffset);
            scrollOffsetRef.current = targetOffset;
            onChangeIndex(i);
          }
        }}
        hasSection={!!slides[activeIndex].sectionId}
        hovered={hoveredRef.current}
        slides={slides}
      />
    </div>
  );
}
